/**
 * ProofAttach —— **通用憑證上載**（影相 or 揀檔案；可多張）
 * 用於所有交易（報銷／收入／應收應付開單…）：每筆都要有「憑證」畀會計師／管理層用。
 * ⚠️ 只係 UI ＋ 上載 R2；**綁定憑單由建立交易嗰個 API 做**（`attachmentIds`）。
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cameraAvailable } from '@coeldery/camera-capture/index.ts';
import CameraView from './CameraView';
import { api, type Attachment } from '../lib/api';

export interface Proof { id: string; mime: string; bytes: number }

export default function ProofAttach({
  proofs, onChange, disabled,
}: {
  proofs: Proof[];
  onChange: (next: Proof[]) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const [camera, setCamera] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const uploadBlob = async (blob: Blob, name: string, w?: number, h?: number) => {
    setBusy(true); setErr(null);
    try {
      const r: { attachment: Attachment } = await api.upload(blob, name, w, h);
      onChange([...proofs, { id: r.attachment.id, mime: r.attachment.mime, bytes: r.attachment.bytes }]);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="proof">
      {camera && (
        <CameraView
          onClose={() => setCamera(false)}
          onCaptured={(blob, w, h) => { setCamera(false); void uploadBlob(blob, `proof-${Date.now()}.jpg`, w, h); }}
        />
      )}
      <div className="proof-list">
        {proofs.map((p) => (
          <span key={p.id} className="proof-thumb">
            {p.mime === 'application/pdf'
              ? <a href={`/api/attachments/${p.id}`} target="_blank" rel="noreferrer">📄 PDF</a>
              : <img src={`/api/attachments/${p.id}`} alt="" />}
            <button type="button" className="proof-del" onClick={() => onChange(proofs.filter((x) => x.id !== p.id))} aria-label="remove">×</button>
          </span>
        ))}
      </div>
      <div className="row">
        <button
          type="button" className="cta secondary"
          disabled={disabled || busy || !cameraAvailable(navigator)}
          onClick={() => setCamera(true)}
        >
          📷 {t('proof.camera')}
        </button>
        <label className="cta secondary">
          📎 {t('proof.file')}
          <input type="file" accept="image/*,application/pdf" hidden disabled={disabled || busy}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void uploadBlob(f, f.name); }} />
        </label>
      </div>
      {busy && <p className="note">{t('proof.uploading')}</p>}
      {err && <p className="err">{err}</p>}
      <p className="note">{t('proof.hint')}</p>
    </div>
  );
}
